import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { Subject, merge, of } from "rxjs";
import { filter, switchMap, takeUntil } from "rxjs/operators";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { AtgardStatusesDTO, VKAtgardStatusAvverkningsanmalanDTO } from "../../dto/atgardstatuses/atgardstatuses";
import { IVKAtgardKedjaKoppling } from "../../models/Atgardskedjor/atgardskedjor-models";
import { AtgardStatusAvverkningsanmalan, AtgardStatuses } from "../../models/atgardstatus/atgardstatuses-models";
import { Avverkningsanmalan } from "../../models/avverkningsanmalan-models";
import { AtgardNaturvardesbedomningDTO } from "../../models/naturvardesbedomning-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { AtgardKedjaMediatorService } from "../../services/atgardkedja-mediator.service";
import { AvtalService } from "../../services/avtal.service";
import { AvverkningsanmalanMediatorService } from "../../services/avverkningsanmalan-mediator.service";
import { AvverkningsanmalanService } from "../../services/avverkningsanmalan.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { NavigationService } from "../../services/navigation.service";
import { NvbMediatorService } from "../../services/nvb-mediator.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";
import { AtgardHeaderIconComponent } from "./atgard-header-icon/atgard-header-icon.component";
import { AvvAtgardStatus } from "./models/AvvAtgardStatus";
import { DrivningspunkterAtgardStatus } from "./models/DrivningspunkterAtgardStatus";
import { NvbAtgardStatus, NvbLegacyAtgardStatus } from "./models/NvbAtgardStatus";
import { ProduktionsdatumAtgardStatus } from "./models/ProduktionsdatumAtgardStatus";
import { RelateradAtgardStatus } from "./models/RelateradAtgardStatus";
import { RiskbedomningAtgardStatus } from "./models/RiskbedomningAtgardStatus";
import { IAtgardStatus } from "./models/atgardheadericons-models";

@Component({
    selector: "rask-atgard-header-icons",
    templateUrl: "./atgard-header-icons.component.html",
    styleUrls: ["./atgard-header-icons.component.scss"],
    providers: [
        NvbMediatorService,
        AvverkningsanmalanMediatorService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BusySpinnerComponent,
        AtgardHeaderIconComponent
    ]
})

export class AtgardHeaderIconsComponent implements OnInit, OnDestroy {
    destroyed$ = new Subject<void>();
    loading: boolean;
    status: AtgardStatuses;
    statuses: IAtgardStatus[] = [];

    private readonly atgardKedjaMediatorService = inject(AtgardKedjaMediatorService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly avtalService = inject(AvtalService);
    private readonly avverkningsanmalanMediator = inject(AvverkningsanmalanMediatorService);
    private readonly avverkningsanmalanService = inject(AvverkningsanmalanService);
    private readonly kartaMediatorService = inject(VkKartaMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly navigationService = inject(NavigationService);
    private readonly nvbMediatorService = inject(NvbMediatorService);

    private atgardKedja: IVKAtgardKedjaKoppling[];

    get atgard(): Atgard {
        return this.atgardMediatorService.atgard;
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    ngOnInit(): void {
        this.registerListeners();
    }

    private registerListeners(): void {
        this.atgardMediatorService.atgard$
            .pipe(
                switchMap(atg => {
                    this.loading = true;
                    if(ObjectUtils.hasValue(atg.id)) {
                        return this.avtalService.getAtgardStatuses(atg.id);
                    }
                    return of(new AtgardStatuses(new AtgardStatusesDTO()));
                }),
                takeUntil(this.destroyed$)
            ).subscribe({
                next: atgstatus => {
                    this.loading = false;
                    this.status = atgstatus;
                    this.renderIcons(this.status);
                },
                error: () => this.loading = false
            });

        this.atgardMediatorService.atgardUpdate$
            .pipe(takeUntil(this.destroyed$)).subscribe(atg => {
                this.updateStatusesFromAtgard(atg);
            });

        this.nvbMediatorService.nvbUpdate$
            .pipe(takeUntil(this.destroyed$)).subscribe(nvb => {
                this.updateStatusesFromNvb(nvb);
            });

        this.avverkningsanmalanMediator.avverkningsanmalanUpdated$
            .pipe(filter(y => ObjectUtils.hasValue(y)), takeUntil(this.destroyed$))
            .subscribe(x => {
                this.updateStatusesFromAvverkningsanmalan(x);

            });

        merge(this.kartaMediatorService.atgardDrivningspunktAdded$,
            this.kartaMediatorService.atgardDrivningspunktDeleted$,
            this.kartaMediatorService.atgardDrivningspunktChanged$,
            this.kartaMediatorService.atgardGeometriDeleted$)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.updateStatusesFromDrivningspunkter();
            });

        this.atgardKedjaMediatorService.kontraktKedjor$
            .pipe(takeUntil(this.destroyed$))
            .subscribe(uppdated => {
                if (!uppdated) {
                    return;
                }
                this.atgardKedja = this.atgardKedjaMediatorService.getAtgardKedjaKopplingarByAtgard(this.atgard.guid);
                if (this.status) {
                    this.renderIcons(this.status);
                }
            });
    }

    private renderIcons(atgstatus: AtgardStatuses): void {
        if (!atgstatus) {
            return;
        }

        const canHandleAnmalan = this.avverkningsanmalanService.canHandleAvverkningsanmalan(this.atgard);
        const isSkogsvard = this.atgardMediatorService.isSkogsvard();
        let statuses: IAtgardStatus[];
        if (isSkogsvard) {
            statuses = [new RelateradAtgardStatus(atgstatus, this.navigationService, this.atgardKedja, this.kontraktMediatorService)];
        } else{
            statuses = [
                new NvbLegacyAtgardStatus(atgstatus),
                new NvbAtgardStatus(atgstatus),
                new AvvAtgardStatus(atgstatus, canHandleAnmalan),
                new RelateradAtgardStatus(atgstatus, this.navigationService, this.atgardKedja, this.kontraktMediatorService),
                new RiskbedomningAtgardStatus(atgstatus),
                new ProduktionsdatumAtgardStatus(atgstatus),
                new DrivningspunkterAtgardStatus(atgstatus)
            ];
        }
        this.statuses = statuses;
    }

    private updateStatusesFromAtgard(atgard: Atgard): void {
        if (ObjectUtils.isNullOrUndefined(this.status)) {
            return;
        }
        this.status.foreslagetProduktionsdatumFrom = atgard.foreslagetProduktionsdatumFrom;
        this.status.foreslagetProduktionsdatumTom = atgard.foreslagetProduktionsdatumTom;

        this.status.extraKartinfo.fjallnara = atgard.extraKartInfo?.fjallnara;
        this.status.extraKartinfo.iflOmrade = atgard.extraKartInfo?.iflOmrade;
        this.status.extraKartinfo.rennaringAretRunt = atgard.extraKartInfo?.rennaringAretRunt;
        this.status.extraKartinfo.renskotselOmraden = atgard.extraKartInfo?.renskotselOmraden;

        this.renderIcons(this.status);
    }

    private updateStatusesFromAvverkningsanmalan(x: Avverkningsanmalan) {
        if (!ObjectUtils.hasValue(this.status?.avverkningsanmalan)) {
            this.status.avverkningsanmalan = new AtgardStatusAvverkningsanmalan(new VKAtgardStatusAvverkningsanmalanDTO());
        }
        this.status.avverkningsanmalan.avverkningsanmalanTyp = x.typ;
        this.status.avverkningsanmalan.farStartaDatum = DateUtils.formatDateString(x.avverkningFarStartaDatum, DateUtils.dateformat);
        this.status.avverkningsanmalan.giltigtTillDatum = DateUtils.formatDateString(x.giltigtTillDatum, DateUtils.dateformat);
        this.status.avverkningsanmalan.godkantDatum = DateUtils.formatDateString(x.godkantDatum, DateUtils.dateformat);
        this.renderIcons(this.status);
    }

    private updateStatusesFromDrivningspunkter(): void {
        if (ObjectUtils.isNullOrUndefined(this.status)) {
            return;
        }
        const drivningspunkter = this.kartaMediatorService.getAllDrivningspunkterForAtgard(this.kontraktMediatorService.kontrakt.guid, this.atgard.guid);
        const avlagg = drivningspunkter?.filter(x => x.isAvlagg);
        this.status.avlaggCount = avlagg?.length ?? 0;
        this.status.avlaggAllTillganglighetsklass = avlagg?.every(a => a.kartgeometri?.tillganglighetsklass?.length > 0);
        this.status.larmPunkterCount = drivningspunkter?.filter(x => x.isLarmpunkt)?.length ?? 0;
        this.renderIcons(this.status);
    }

    private updateStatusesFromNvb(nvb: AtgardNaturvardesbedomningDTO): void {
        if (ObjectUtils.isNullOrUndefined(this.status)) {
            return;
        }
        this.status.naturvardesbedomning.nvbKravsEj = nvb.nvbKravsEj;
        this.status.naturvardesbedomning.utfordAv = nvb.utfordAv;
        this.status.naturvardesbedomning.utfordDatum = nvb.utfordDatum;
        this.renderIcons(this.status);
    }
}
